import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Search, Target, Users, Shield, Zap, CheckCircle2, Award, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';

const SERVICES = [
  {
    id: 'retained-search',
    icon: Target,
    title: 'Retained Executive Search',
    tagline: 'Finding the right HEAD, not just headcount',
    description: 'Deep industry and functional knowledge combined with proven 8-step executive search process to find leaders who best fit your unique needs and culture.',
    approach: [
      { step: 'Understand Client Position', detail: 'Deep dive into role requirements, culture, and strategic context' },
      { step: 'Research & Long Listing', detail: 'Comprehensive market research and talent mapping' },
      { step: 'Mapping & Short Listing', detail: 'Assess and shortlist candidates against defined criteria' },
      { step: 'Present to Client', detail: 'Detailed candidate profiles with competency assessment' },
      { step: 'Candidate Fitment & Validation', detail: 'Psychometric tools and competency-based interviews' },
      { step: 'Support Negotiation & Closure', detail: 'Facilitate offer discussion and acceptance' },
      { step: 'Support Selection Process', detail: 'Coordinate interviews and assessments' },
      { step: 'Handholding & Joining', detail: 'Onboarding support and transition management' }
    ],
    deliverables: ['Comprehensive market mapping', 'Detailed candidate profiles', 'Competency assessments', 'Interview coordination', 'Offer negotiation support'],
    outcomes: 'Perfect role-culture fit, reduced time to hire, successful leadership transitions',
    caseStudy: { client: 'Sleep Solutions Brand', result: '3 critical leadership hires in 4 months, 100% role-culture fit' }
  },
  {
    id: 'rpo',
    icon: Users,
    title: 'Recruitment Process Outsourcing',
    tagline: 'End-to-end recruitment excellence',
    description: 'Complete recruitment management with SLA adherence, employer branding, and project management excellence through our structured 8-step RPO model.',
    approach: [
      { step: 'Signing Off JDs', detail: 'Collaborate on detailed job descriptions and requirements' },
      { step: 'Advertisements & Employer Branding', detail: 'Create compelling job postings and strengthen employer brand' },
      { step: 'Talent Assessment & Mapping', detail: 'Identify and assess talent pools systematically' },
      { step: 'Sourcing & Screening', detail: 'Multi-channel sourcing and rigorous screening process' },
      { step: 'Interview Coordination', detail: 'Manage complete interview logistics and scheduling' },
      { step: 'Offer Management', detail: 'Handle offer creation, negotiation, and acceptance' },
      { step: 'Onboarding', detail: 'Smooth onboarding process and documentation' },
      { step: 'Project Management', detail: 'Regular reporting, SLA tracking, and continuous improvement' }
    ],
    deliverables: ['Complete recruitment operations', 'SLA adherence & reporting', 'Employer branding support', 'Candidate pipeline management'],
    outcomes: 'Reduced hiring time, improved quality of hire, cost efficiency, scalability',
    caseStudy: { client: 'Multiple Organizations', result: 'Consistent SLA achievement, improved candidate quality, reduced cost per hire' }
  },
  {
    id: 'talent-mapping',
    icon: Search,
    title: 'Talent Mapping & Market Intelligence',
    tagline: 'Strategic talent insights for competitive advantage',
    description: 'Comprehensive market mapping to identify talent pools, benchmark competitors, and build strategic talent intelligence for informed decision-making.',
    approach: [
      { step: 'Market Intelligence Gathering', detail: 'Research industry trends, salary benchmarks, and talent availability' },
      { step: 'Competitor Benchmarking', detail: 'Analyze competitor talent strategies and organizational structures' },
      { step: 'Talent Pool Identification', detail: 'Map key talent pools across industries and geographies' },
      { step: 'Skills & Salary Mapping', detail: 'Detailed analysis of skill requirements and compensation trends' }
    ],
    deliverables: ['Market intelligence reports', 'Competitor talent analysis', 'Talent pool mapping', 'Salary benchmarking data'],
    outcomes: 'Strategic hiring decisions, competitive positioning, talent pipeline development',
    caseStudy: { client: 'Packaging Company', result: 'Visionary MD hired in 90 days through precise talent mapping' }
  }
];

const ADVANTAGES = [
  { icon: Award, title: 'Industry Exposure', description: 'Diverse exposure across Banking, Consumer, Healthcare, Industrial, Pharma, Technology, Telecom, and more' },
  { icon: Users, title: 'Extensive Networking', description: 'Access to vast pool of candidates including passive talent through deep industry networks' },
  { icon: Zap, title: 'Scientific Search', description: 'Psychometric tools and competency-based interviews for accurate candidate assessment' },
  { icon: Target, title: 'Success-Driven', description: 'Find candidates aligned with goals and culture to drive organizational success' },
  { icon: Search, title: 'Robust Research', description: 'Comprehensive market mapping and faster TAT for competitive edge' },
  { icon: Shield, title: 'Integrity & Confidentiality', description: 'Discreet searches with strict no-poach policy to protect client interests' }
];

export default function ExecutiveSearchPage() {
  const [expandedService, setExpandedService] = useState(null);

  return (
    <div className="bg-[#FAF5EF]">
      {/* Hero */}
      <section className="relative py-24 lg:py-32 bg-gradient-to-br from-[#1A1008] to-[#3D1C11] text-white">
        <div className="absolute inset-0 opacity-[0.03]">
          <div className="absolute top-20 right-20 w-96 h-96 bg-[#E8642C] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="max-w-[900px]">
            <p className="text-[11px] font-semibold text-[#E8642C] tracking-[0.25em] uppercase mb-6">Executive Search</p>
            <h1 className="text-[clamp(3rem,6vw,5.5rem)] font-bold leading-[1.05] tracking-[-0.02em] mb-8 text-white">
              It's not the headcount.<br/>It's the HEAD that counts.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-12">
              We combine deep industry knowledge with a proven executive search process to find leaders who best fit your organization's unique needs and culture.
            </p>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                { value: '3', label: 'Leadership Hires', sublabel: '4 months (Sleep Solutions)' },
                { value: '100%', label: 'Role-Culture Fit', sublabel: 'Executive Search Success' },
                { value: '90 days', label: 'Time to Closure', sublabel: 'Packaging MD Search' },
                { value: '50+', label: 'Assessed Per Role', sublabel: 'Rigorous Process' }
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-3xl lg:text-4xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-xs text-white/60 uppercase tracking-wide mb-1">{stat.label}</div>
                  <div className="text-xs text-white/40">{stat.sublabel}</div>
                </div>
              ))}
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/contact" className="inline-flex items-center gap-2 bg-[#E8642C] text-white px-8 py-4 rounded-full text-sm font-semibold hover:bg-[#CC2929] transition-all shadow-lg">
                Discuss Your Search
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">Our Services</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">
              Three comprehensive talent acquisition solutions. Click to explore each in detail.
            </p>
          </div>

          <div className="space-y-6">
            {SERVICES.map((service, i) => (
              <DetailedServiceCard 
                key={service.id} 
                service={service} 
                index={i}
                isExpanded={expandedService === service.id}
                onToggle={() => setExpandedService(expandedService === service.id ? null : service.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Anexi Advantage */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1A1008] mb-6">The Anexi Advantage</h2>
            <p className="text-lg text-[#6B5B50] max-w-[700px] mx-auto">What sets our executive search practice apart.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {ADVANTAGES.map((advantage, i) => (
              <div key={i} className="flex gap-4 bg-white rounded-2xl p-6 border border-[#3D1C11]/10">
                <div className="flex-shrink-0 w-10 h-10 bg-[#E8642C]/10 rounded-xl flex items-center justify-center">
                  <advantage.icon className="w-5 h-5 text-[#E8642C]" />
                </div>
                <div>
                  <h4 className="font-bold text-[#1A1008] mb-2">{advantage.title}</h4>
                  <p className="text-sm text-[#6B5B50] leading-relaxed">{advantage.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries & Mandates */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h3 className="text-3xl font-bold text-[#1A1008] mb-8">Industry Practices</h3>
              <div className="grid grid-cols-2 gap-4">
                {['Banking & Finance', 'Consumer Corporate', 'Healthcare', 'Industrial & Engineering', 'Pharma & Healthcare', 'PSU', 'Technology & Media', 'Telecom'].map((industry, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#E8642C] flex-shrink-0" />
                    <span className="text-[#6B5B50]">{industry}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-3xl font-bold text-[#1A1008] mb-8">Executed Mandates</h3>
              <div className="grid grid-cols-2 gap-4">
                {['CFO / CMO / CHRO', 'CEO / COO', 'President / VP', 'Director', 'General Manager', 'National & Regional Head', 'Managing Director'].map((role, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#E8642C] flex-shrink-0" />
                    <span className="text-[#6B5B50]">{role}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1000px] px-6 sm:px-8 lg:px-12">
          <div className="bg-white rounded-3xl p-10 lg:p-16 shadow-xl">
            <div className="text-center mb-10">
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A1008] mb-6">Find your next leader</h2>
              <p className="text-lg text-[#6B5B50] max-w-[600px] mx-auto">Let's discuss your executive search requirements.</p>
            </div>
            <LeadForm source="executive_search_page" />
          </div>
        </div>
      </section>
    </div>
  );
}

function DetailedServiceCard({ service, index, isExpanded, onToggle }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  const Icon = service.icon;
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      className="bg-white rounded-3xl border border-[#3D1C11]/10 hover:border-[#E8642C]/30 transition-all overflow-hidden"
    >
      <button
        onClick={onToggle}
        className="w-full p-8 lg:p-10 flex items-start gap-6 text-left hover:bg-[#FAF5EF]/50 transition-colors"
      >
        <div className="flex-shrink-0 w-14 h-14 bg-[#E8642C]/10 rounded-2xl flex items-center justify-center">
          <Icon className="w-7 h-7 text-[#E8642C]" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-2xl font-bold text-[#1A1008] mb-2">{service.title}</h3>
              <p className="text-sm text-[#E8642C] font-medium mb-3">{service.tagline}</p>
              <p className="text-[#6B5B50] leading-relaxed">{service.description}</p>
            </div>
            <motion.div
              animate={{ rotate: isExpanded ? 180 : 0 }}
              transition={{ duration: 0.3 }}
              className="flex-shrink-0"
            >
              <ChevronDown className="w-6 h-6 text-[#6B5B50]" />
            </motion.div>
          </div>
        </div>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden"
          >
            <div className="px-8 lg:px-10 pb-10 pt-4 border-t border-[#3D1C11]/10 bg-[#FAF5EF]/30">
              <div className="grid lg:grid-cols-2 gap-10">
                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Our Process
                  </h4>
                  <div className="space-y-4">
                    {service.approach.map((step, i) => (
                      <div key={i} className="flex gap-3">
                        <div className="flex-shrink-0 w-8 h-8 bg-[#E8642C]/10 rounded-lg flex items-center justify-center text-[#E8642C] font-bold text-sm">
                          {i + 1}
                        </div>
                        <div>
                          <div className="font-bold text-[#1A1008] text-sm mb-1">{step.step}</div>
                          <div className="text-xs text-[#6B5B50] leading-relaxed">{step.detail}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-bold text-[#1A1008] mb-6 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Deliverables
                  </h4>
                  <div className="space-y-2 mb-8">
                    {service.deliverables.map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-[#E8642C] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#6B5B50]">{item}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className="text-lg font-bold text-[#1A1008] mb-4 flex items-center gap-2">
                    <div className="w-1 h-5 bg-[#E8642C] rounded-full" />
                    Expected Outcomes
                  </h4>
                  <p className="text-sm text-[#E8642C] font-medium mb-6">{service.outcomes}</p>

                  {service.caseStudy && (
                    <div className="bg-white rounded-xl p-5 border border-[#E8642C]/20">
                      <div className="text-xs font-bold text-[#1A1008] uppercase tracking-wide mb-2">Case Study</div>
                      <div className="text-sm text-[#6B5B50]">
                        <span className="font-semibold text-[#1A1008]">{service.caseStudy.client}:</span> {service.caseStudy.result}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}